import { FC, useEffect, useLayoutEffect, useState } from 'react';

import { FullscreenOutlined, FullscreenExitOutlined, QuestionOutlined, CloseOutlined, SaveOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Button, Dropdown, Form, Input, MenuProps, Modal, Popover, Tooltip } from 'antd';
import axios from 'axios';
import moment from 'moment/moment';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { trackEvent } from '../../../../../helpers/utils/analytics';
import {
  formatPeriod,
  formatPeriodDate,
  getFiltersWithoutSegment,
} from '../../../../../helpers/utils/functions';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../../../helpers/utils/ui';
import { tgApi } from '../../../api/endpoints/tgApi';
import { axiosConfigAuth } from '../../../api/middleware';
import { API_UN_REPORT_EXEL } from '../../../api/urls';
import { QUERY_NAME_DASHBOARD } from '../../../constants/query-params';
import { STATIC_LINK_DRAWER } from '../../../constants/staticLinkConstants';
import { Roles } from '../../../constants/user';
import { useActions } from '../../../hooks/useActions';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import { useSaveReportShortcut } from '../../../hooks/useSaveReportShortcut';
import { ReportMenu } from '../../nav/components/reportMenu/reportMenu';
import { compareDescription } from '../../../constants/tooltips';

const HELP_LINK_REPORTS = 'https://doc.scananalytics.ru/otchety/tipy-otchetov-i-chem-oni-otlichayutsya/';

const HELP_LINKS = {
  1: HELP_LINK_REPORTS,
  2: HELP_LINK_REPORTS,
  3: HELP_LINK_REPORTS,
  4: HELP_LINK_REPORTS,
  5: HELP_LINK_REPORTS,
};

const getReportName = (pathName: string, name?: string) => {
  switch (pathName) {
    case '/universal':
      return name? `Отчёт по регистрациям: ${name}`: 'Новый отчёт по регистрациям';
    case '/order-reports':
      return name?  `Отчёт по заказам: ${name}` : 'Новый отчёт по заказам';
    case '/payment-reports':
      return name?  `Отчёт по платежам: ${name}` : 'Новый отчёт по платежам';
    case '/ad-reports':
      return name?  `Отчёт по рекламе: ${name}` : 'Новый отчёт по рекламе';
    case '/webinar-reports':
      return name?  `Отчёт по вебинарам: ${name}` : 'Новый отчёт по вебинарам';
    default:
      return name?  `Отчёт: ${name}` : 'Новый отчёт';
  }
};

const UniversalReportTitle: FC = () => {
  const location = useLocation();
  const [ searchParams, setSearchParams ] = useSearchParams();
  const {
    setAppReportOpen,
    setDrawerLink,
    postReport,
    putReport,
    setMetaCompare,
    setMetaFullscreen,
    setMetaTableLoading,
    setMetaChartLoading,
    setMetaTableFetching,
    setMetaChartFetching,
  } = useActions();
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  
  const {
    currentReport,
  } = useTypedSelector((state) => state.reports);
  const period = useTypedSelector((state) => state.period);
  const { details } = useTypedSelector((state) => state.details);
  const { filters, currentSegment, currentFunnel } = useTypedSelector((state) => state.filter);
  const { attributes } = useTypedSelector((state) => state.attributes);
  const { chartType, isChartVisible, isAxesVisible, cumulativeChart } = useTypedSelector((state) => state.chart);
  const {
    metrics,
    groupings,
    groupsChecked,
    metricsChecked,
    filtersTable,
    sortTable,
    groupsSelectionMode,
    selectFirstNRows,
    totalChecked,
    percentages,
    sparklines,
    cellFills,
    frozensColumns,
  } = useTypedSelector((state) => state.table);
  const { isCompare, isFullscreen } = useTypedSelector((state) => state.meta);
  const [ isModalVisible, setIsModalVisible ] = useState(false);
  const [ isModalXLSXVisible, setIsModalXLSXVisible ] = useState(false);
  const [ isHaveTaskInTg, setIsHaveTaskInTg ] = useState(false);
  const [ reportName, setReportName ] = useState('');
  const [ excelName, setExcelName ] = useState('');
  const [ isDisableSave, setIsDisableSave ] = useState(false);
  const { savedReports } = useTypedSelector((state) => state.reports);
  const { role } = useTypedSelector((state) => state.currentUser);

  // Обогащаем currentReport данными из savedReports (is_favorite, access_id, user_id и т.д.)
  const savedReportData = savedReports.find(
    (r: any) => r.id === currentReport.id || r.hash === currentReport.hash
  );
  const reportMenuItem = currentReport.id && savedReportData
    ? { ...savedReportData, ...currentReport }
    : currentReport;

  const navigate = useNavigate();

  // Хук для сохранения отчёта по Ctrl+S / Cmd+S
  // При невозможности сохранить — открываем модалку "Сохранить как"
  useSaveReportShortcut({ onSaveAs: () => setIsModalVisible(true) });

  // Проверка прав доступа к отчёту
  useLayoutEffect(() => {
    const hashId = searchParams.get(QUERY_NAME_DASHBOARD);
    const report = savedReports.find((report) => {
      return report.hash === hashId;
    });
    if (report?.access_id <= Roles[role]) {
      setIsDisableSave(false);
    } else {
      setIsDisableSave(true);
    }
  }, [searchParams, savedReports, role]);


  const handleOpenHelpFrame = () => {
    const link = HELP_LINKS[currentReport.report_id as keyof typeof HELP_LINKS] ?? STATIC_LINK_DRAWER.reports;
    setDrawerLink(link);
    setAppReportOpen(true);
  };

  const save = () => {
    const current_un_period_selection = period.select;
    let values: any = {
      ...currentReport.values,
      period: {
        start: formatPeriodDate(period.start, period.withTime),
        end: formatPeriodDate(period.end, period.withTime),
        interval: period.interval || 'day',
      },
      details,
      chartType,
      groupings,
      metrics,
      groupsChecked,
      metricsChecked,
      filtersTable,
      sortTable,
      attributes,
      current_un_period_selection: current_un_period_selection
        ? current_un_period_selection
        : '',
      segment: null,
      filtersReq: attributes,
      // Сохраняем настройки автовыбора группировок
      groupsSelectionMode: groupsSelectionMode || 'manual',
      selectFirstNRows: selectFirstNRows,
      // Сохраняем состояние "Итого и среднее"
      totalChecked: totalChecked,
      // Сохраняем воронку продаж
      currentFunnel: currentFunnel,
      // Сохраняем настройки графика
      isChartVisible: isChartVisible,
      isAxesVisible: isAxesVisible,
      cumulativeChart: cumulativeChart,
      // Сохраняем процент в колонках и закреплённые столбцы
      percentages: percentages,
      // Колонки со спарклайном
      sparklines: sparklines,
      // Заливка ячеек обычных метрик по условиям «от – до»
      cellFills: cellFills,
      frozensColumns: frozensColumns,
    };

    if (Object.keys(currentSegment || {}).length > 0) {
      values.segment = currentSegment;
      values.filters = getFiltersWithoutSegment(
        filters,
        currentSegment?.values?.filters
      );
    } else {
      values.segment = {};
      values.filters = filters;
      values.attributes = attributes;
    }

    if (selectedSchool?.id) {
      if (period.interval === 'ndaysago' || period.select === 'ndaysago') {
        values.period.days = period.days;
      }
      trackEvent('report-save', { mode: 'update', report_path: location.pathname });
      putReport(selectedSchool.id, { id: currentReport.id, values });
    }
  };

  const saveAs = () => {
    if (selectedSchool?.id) {
      const current_un_period_selection = period.select;
      let values: any = {
        name: reportName,
        period: {
          start: formatPeriodDate(period.start, period.withTime),
          end: formatPeriodDate(period.end, period.withTime),
          interval: period.interval || period.select,
          select: period.select || period.interval,
        },
        details,
        chartType,
        groupings,
        metrics,
        groupsChecked,
        metricsChecked,
        filtersTable,
        sortTable,
        attributes,
        filtersReq: attributes,
        current_un_period_selection: current_un_period_selection
          ? current_un_period_selection
          : '',
        segment: null,
        // Сохраняем настройки автовыбора группировок
        groupsSelectionMode: groupsSelectionMode || 'manual',
        selectFirstNRows: selectFirstNRows,
        // Сохраняем состояние "Итого и среднее"
        totalChecked: totalChecked,
        // Сохраняем воронку продаж
        currentFunnel: currentFunnel,
        // Сохраняем настройки графика
        isChartVisible: isChartVisible,
        isAxesVisible: isAxesVisible,
        cumulativeChart: cumulativeChart,
        // Сохраняем процент в колонках и закреплённые столбцы
        percentages: percentages,
        // Колонки со спарклайном
        sparklines: sparklines,
        // Заливка ячеек обычных метрик по условиям «от – до»
        cellFills: cellFills,
        frozensColumns: frozensColumns,
      };

      if (Object.keys(currentSegment || {}).length > 0) {
        values.segment = currentSegment;
        values.filters = getFiltersWithoutSegment(
          filters,
          currentSegment?.values?.filters
        );
      } else {
        let _filters = filters;
        if (!Array.isArray(filters)) {
          _filters = [];
        }
        values.filters = _filters;
        values.attributes = attributes;
      }
      if (period.interval === 'ndaysago' || period.select === 'ndaysago') {
        values.period.days = period.days;
      }
      const onSuccess = (report_id: number) => {
        navigate(window.location.pathname + '?report_id=' + report_id);
      };
      window.history.replaceState({}, document.title, window.location.pathname);
      trackEvent('report-save', { mode: 'create', report_path: location.pathname });
      postReport(selectedSchool.id, { values, onSuccess });
      setIsModalVisible(false);
      setReportName('');
    }
  };

  const saveXSLX = () => {
    const format = 'YYYY-MM-DD';
    const _period = {
      ...formatPeriod({ start: period.start, end: period.end }, format),
    };
    const data = {
      name: excelName,
      period: _period,
      filters: attributes,
      groupings,
      metrics,
    };

    trackEvent('report-export-excel', {
      report_path: location.pathname,
      metrics_count: (metrics ?? []).length,
      groupings_count: (groupings ?? []).length,
    });

    axios
      .post(API_UN_REPORT_EXEL, data, axiosConfigAuth())
      .then(() => {
        showSuccessMessage('Сохранено');
      })
      .catch(() => {
        showErrorMessage('Ошибка при сохранении');
      });

    setIsModalXLSXVisible(false);
    setExcelName('');
  };

  const handleUpdateTaskStatus = () => {
    if (!location.pathname.includes('/webinar/quality') && currentReport.id) {
      tgApi.isExistTask(currentReport.id).then((data) => {
              Boolean(data) !== isHaveTaskInTg && setIsHaveTaskInTg(Boolean(data));
          });
      }
  };

  useEffect(() => {
    handleUpdateTaskStatus();
  }, [ currentReport.id ]);

  // Обработчик полноэкранного режима
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setMetaFullscreen(true);
      }).catch(() => {
        // Fullscreen не поддерживается или заблокирован
      });
    } else {
      document.exitFullscreen().then(() => {
        setMetaFullscreen(false);
      }).catch(() => {
        // Ошибка выхода из fullscreen
      });
    }
  };

  // Слушатель изменения состояния fullscreen (для выхода через Escape)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setMetaFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [setMetaFullscreen]);

  // Горячие клавиши для полноэкранного режима: F11 или Shift+Ctrl+F
  useEffect(() => {
    const handleFullscreenKey = (e: KeyboardEvent) => {
      const isShiftCtrlF = e.shiftKey && (e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F');
      if (e.key === 'F11' || isShiftCtrlF) {
        e.preventDefault();
        toggleFullscreen();
      }
    };
    document.addEventListener('keydown', handleFullscreenKey);
    return () => document.removeEventListener('keydown', handleFullscreenKey);
  }, [toggleFullscreen]);

  const savingItemsDropDown: MenuProps['items'] = [
    {
      key: '1',
      label: Object.keys(currentReport).length > 0 && !isDisableSave && (
        <span onClick={save}>Сохранить изменения</span>
      ),
    },
    {
      key: '2',
      label: <span onClick={() => setIsModalVisible(true)}>Сохранить как новый отчет</span>,
    },
  ];
  const reportCurrName =  getReportName(location.pathname.replace('users/', ''), currentReport?.values?.name);
  return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '8px',
          }}>
            <div data-help-id="report-title" style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1a1a2e',
            }}>
                {reportCurrName}
            </div>
            {currentReport.id && (
              <ReportMenu isDashboard={false} item={reportMenuItem} trigger='click' isHaveTaskReportInTg={isHaveTaskInTg} setReloadTask={handleUpdateTaskStatus}/>
            )}
            <Tooltip title="Как работать с отчетом?">
              <button
                data-help-id="report-help"
                onClick={handleOpenHelpFrame}
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: '#ff4d4f',
                  fontSize: 13,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#fff1f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <QuestionOutlined style={{ background: '#ff4d4f', color: 'white', borderRadius: '50%', padding: 3, fontSize: 10 }}/>
                <span>Как работать с отчетом {getReportName(location.pathname.replace('users/', '')).replace('Новый отчёт', '').replace(':', '?')}</span>
              </button>
            </Tooltip>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Кнопка Горячие клавиши */}
            <Popover
              trigger="click"
              placement="bottomRight"
              showArrow={false}
              content={
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 340 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, padding: '6px 10px', background: '#fafafa', borderRadius: '6px' }}>
                    <span style={{ fontSize: 13 }}>Сохранить отчёт</span>
                    <span style={{ whiteSpace: 'nowrap' }}><kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Ctrl</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>S</kbd></span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, padding: '6px 10px', background: '#fafafa', borderRadius: '6px' }}>
                    <span style={{ fontSize: 13 }}>Показать данные</span>
                    <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Enter</kbd>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, padding: '6px 10px', background: '#fafafa', borderRadius: '6px' }}>
                    <span style={{ fontSize: 13 }}>Полноэкранный режим</span>
                    <span style={{ whiteSpace: 'nowrap' }}><kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Shift</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Ctrl</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>F</kbd></span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, padding: '6px 10px', background: '#fafafa', borderRadius: '6px' }}>
                    <span style={{ fontSize: 13 }}>Выход из полноэкранного</span>
                    <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Esc</kbd>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, padding: '6px 10px', background: '#fafafa', borderRadius: '6px' }}>
                    <span style={{ fontSize: 13 }}>Сброс: очистка кеша, локальных хранилищ и перезагрузка</span>
                    <span style={{ whiteSpace: 'nowrap' }}><kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Ctrl</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Shift</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>K</kbd></span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#8c8c8c', padding: '2px 10px' }}>
                    На macOS вместо Ctrl используйте Cmd (⌘)
                  </div>
                </div>
              }
            >
              <button
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: '#8c8c8c',
                  fontSize: 13,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <ThunderboltOutlined style={{ fontSize: 12 }}/>
                <span>Горячие клавиши</span>
              </button>
            </Popover>
            {/* Кнопка Сравнить */}
            {isCompare ? (
              <Button
                data-help-id="report-compare"
                data-analytics-id="report-compare-off"
                icon={<CloseOutlined style={{ color: '#000000' }} />}
                onClick={() => {
                  trackEvent('report-compare-off');
                  setMetaCompare({ value: false });
                  // Возврат к обычному отчёту: в таблице и на графике остаются данные
                  // сравнения, поэтому сразу запрашиваем обычные за выбранный период
                  setMetaTableLoading({ value: true });
                  setMetaChartLoading({ value: true });
                  setMetaTableFetching({ value: true });
                  setMetaChartFetching({ value: true });
                }}
                style={{
                  height: '32px',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  background: '#fff',
                  color: '#333',
                  fontSize: '13px',
                  padding: '0 12px',
                }}
              >
                Закончить сравнение
              </Button>
            ) : (
              <Tooltip
                placement="bottom"
                title={
                  <div
                    className="page-title-tooltip-text"
                    dangerouslySetInnerHTML={{ __html: compareDescription }}
                  ></div>
                }
                color="#fff"
                zIndex={9999}
                trigger="hover"
                overlayClassName="page-title-tooltip"
              >
                <Button
                  data-help-id="report-compare"
                  data-analytics-id="report-compare-on"
                  icon={
                    <svg
                      className="params__compare-icon"
                      version="1.0"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512.000000 512.000000"
                      preserveAspectRatio="xMidYMid meet"
                      style={{ width: 16, height: 16, marginRight: 4, marginBottom: -2 }}
                    >
                      <g
                        transform="translate(0.000000,512.000000) scale(0.100000,-0.100000)"
                        fill="#000000"
                        stroke="none"
                      >
                        <path d="M2488 5099 c-77 -40 -72 22 -78 -974 l-5 -890 -1150 -320 c-632 -176 -1160 -324 -1173 -329 -32 -11 -73 -69 -79 -113 -11 -86 58 -165 144 -165 29 0 161 32 336 81 159 45 292 81 297 81 5 0 -167 -294 -383 -652 l-392 -653 1 -95 c1 -125 25 -241 78 -372 237 -587 924 -860 1492 -594 243 115 414 280 529 511 67 135 101 251 116 398 10 97 9 117 -4 150 -9 20 -208 357 -442 748 -235 391 -424 713 -421 716 7 7 2319 651 2324 648 1 -1 -170 -290 -381 -642 -212 -353 -390 -657 -397 -677 -16 -49 3 -231 36 -345 95 -325 347 -605 655 -727 223 -87 492 -102 718 -38 123 34 290 121 392 204 182 147 318 352 379 572 25 90 46 271 37 318 -4 20 -197 352 -442 760 -239 398 -431 727 -427 731 4 4 180 55 392 113 212 59 396 115 409 124 116 85 77 255 -62 269 -33 3 -282 -62 -1142 -302 -605 -168 -1108 -308 -1117 -311 -17 -5 -18 39 -18 836 0 823 0 842 -20 880 -37 73 -127 99 -202 59z m1868 -2451 l344 -573 -344 -3 c-190 -1 -502 -1 -694 0 l-350 3 346 577 c191 317 348 575 351 573 2 -2 158 -262 347 -577z m-2887 -811 c188 -313 341 -570 341 -573 0 -2 -313 -4 -695 -4 -382 0 -695 3 -695 6 0 6 667 1121 683 1142 6 7 14 9 18 5 4 -4 161 -264 348 -576z" />
                      </g>
                    </svg>
                  }
                  onClick={() => {
                    trackEvent('report-compare-on', { report_path: location.pathname });
                    setMetaCompare({ value: true });
                  }}
                  style={{
                    height: '32px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    background: '#fff',
                    color: '#333',
                    fontSize: '13px',
                    padding: '0 12px',
                  }}
                >
                  Сравнить
                </Button>
              </Tooltip>
            )}
            
            {/* Кнопка Сохранить */}
            {!isCompare && (
              <Dropdown
                overlayStyle={{ left: 1490 }}
                disabled={isCompare}
                menu={{ items: savingItemsDropDown }}
                trigger={[ 'click' ]}
              >
                <Button
                  data-help-id="report-save"
                  icon={<SaveOutlined />}
                  disabled={isCompare}
                  style={{
                    height: '32px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    background: '#fff',
                    color: '#333',
                    fontSize: '13px',
                    padding: '0 12px',
                  }}
                >
                  Сохранить
                </Button>
              </Dropdown>
            )}

            {/* Кнопка Масштабирования */}
            <Tooltip title={isFullscreen ? 'Выйти из полноэкранного режима' : 'Полноэкранный режим'}>
              <Button
                data-help-id="report-fullscreen"
                icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={toggleFullscreen}
                style={{
                  height: '32px',
                  width: '32px',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  background: '#fff',
                  color: '#333',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            </Tooltip>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 0,
            width: '100%',
          }}
        >
          <Modal
            title="Сохранить отчет как"
            open={isModalVisible}
            onOk={() =>
              reportName
                ? saveAs()
                : showErrorMessage('Введите название отчета')
            }
            onCancel={() => setIsModalVisible(false)}
            okText="Сохранить"
          >
            <Form>
              <Form.Item
                name="reportName"
                label="Название отчета"
                rules={[
                  { required: true, message: 'Введите название отчета' },
                  { min: 1, message: 'Введите название отчета' },
                ]}
              >
                <Input
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  size="large"
                  placeholder="Введите название отчета"
                  allowClear
                />
              </Form.Item>
            </Form>
          </Modal>
          <Modal
            title="Скачать XLSX таблицы"
            open={isModalXLSXVisible}
            onOk={saveXSLX}
            onCancel={() => setIsModalXLSXVisible(false)}
            okText="Сохранить"
          >
            <Input
              value={excelName}
              onChange={(e) => setExcelName(e.target.value)}
              size="large"
              placeholder="Введите название файла"
              allowClear
            />
          </Modal>
        </div>
      </>
  );
};

export default UniversalReportTitle;
